import { decodePolyline } from '@motogo/shared';
import { type FC, useMemo } from 'react';

interface Props {
  /** Google-encoded overview polyline (RideSummary.overviewPolyline). */
  polyline: string;
  width?: number;
  height?: number;
}

/**
 * A tiny route thumbnail drawn straight from the overview polyline — no map tiles, instant,
 * offline. Equirectangular projection (lon scaled by cos(meanLat)) so the shape isn't
 * squashed at our latitudes, fit into the box preserving aspect ratio.
 */
const RideSparkline: FC<Props> = ({ polyline, width = 96, height = 64 }) => {
  const path = useMemo(() => buildPath(decodePolyline(polyline), width, height), [polyline, width, height]);

  return (
    <svg className="mg-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {path ? (
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      ) : (
        <circle cx={width / 2} cy={height / 2} r={3} fill="var(--text-faint)" />
      )}
    </svg>
  );
};

function buildPath(points: [number, number][], width: number, height: number): string | null {
  if (points.length < 2) return null;

  const pad = 6;
  const meanLat = points.reduce((s, [lat]) => s + lat, 0) / points.length;
  const kx = Math.cos((meanLat * Math.PI) / 180); // longitude compression at this latitude

  const xs = points.map(([, lon]) => lon * kx);
  const ys = points.map(([lat]) => lat);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const spanX = maxX - minX || 1e-9;
  const spanY = maxY - minY || 1e-9;
  const scale = Math.min((width - 2 * pad) / spanX, (height - 2 * pad) / spanY);

  // Centre the (aspect-preserved) track within the box.
  const offsetX = (width - spanX * scale) / 2;
  const offsetY = (height - spanY * scale) / 2;

  return points
    .map(([lat, lon], i) => {
      const x = offsetX + (lon * kx - minX) * scale;
      // Flip Y: screen y grows downward, latitude grows upward.
      const y = height - offsetY - (lat - minY) * scale;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export default RideSparkline;
